import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAPI } from '../contexts/APIContext';
import logger from '../utils/logger';
import { getStoryGenerationEstimate, getStoryGenerationEstimateSeconds } from '../utils/storyEstimate';

const EpisodeProgressNotification = ({ episodeId, onDismiss, onComplete }) => {
  const { apiClient } = useAPI();
  const [progress, setProgress] = useState(5);
  const [status, setStatus] = useState('processing');
  const [isVisible, setIsVisible] = useState(true);
  const [emailSent, setEmailSent] = useState(false); // Prevent duplicate emails
  const [durationSecs, setDurationSecs] = useState(null); // story target duration → time estimate
  const durationSecsRef = useRef(null); // same value, readable inside the progress timer closure

  // Debug: Log when component mounts
  useEffect(() => {
    console.log('🎬 EpisodeProgressNotification mounted for episode:', episodeId);
    return () => {
      console.log('🎬 EpisodeProgressNotification unmounting for episode:', episodeId);
    };
  }, []);

  useEffect(() => {
    let progressInterval;
    let elapsedTime = 0;
    let isTerminal = false;

    // Poll backend for content status every 5 seconds
    const pollEpisodeStatus = async () => {
      try {
        // Get story status
        const response = await apiClient.getStory(episodeId);

        if (response.status && response.data) {
          const contentStatus = response.data.status;
          const statusPercentage = response.data.status_percentage || 0;
          const dur = response.data.target_duration ?? response.data.duration;
          if (dur) { setDurationSecs(dur); durationSecsRef.current = dur; }
          logger.log('📊 Content status:', contentStatus, 'Status %:', statusPercentage, 'Progress:', progress);

          const failed =
            contentStatus === "FAILED" ||
            contentStatus === "failed" ||
            (contentStatus === "GENERATED" && statusPercentage < 100);

          if (failed) {
            isTerminal = true;
            setStatus('failed');
            clearInterval(progressInterval);
            setTimeout(() => {
              handleDismiss();
            }, 5000);
            return;
          }

          if (contentStatus === 'GENERATED' || contentStatus === 'completed') {
            // Check if it's a success (100%) or failure (<100%)
            if (statusPercentage >= 100) {
              // Success
              isTerminal = true;
              setStatus('completed');
              setProgress(100);
            }

            // Email notification is now handled by video driver on completion

            // Auto-dismiss after appropriate time
            const dismissDelay = statusPercentage >= 100 ? 3000 : 5000;
            setTimeout(() => {
              handleDismiss();
              if (statusPercentage >= 100) {
                onComplete?.();
              }
            }, dismissDelay);
            clearInterval(progressInterval);
          }
        }
      } catch (error) {
        logger.error('Error polling content status:', error);
      }
    };

    // Simulate progress over time (15-20 minutes = 900-1200 seconds)
    // Progress from 5% to 90% over 15 minutes, then wait for actual completion
    const progressIncrement = async () => {
      if (isTerminal) return;
      elapsedTime += 5; // 5 seconds per tick
      // Pace toward the duration-based estimate (falls back to 15 min until we learn it).
      const targetTime = getStoryGenerationEstimateSeconds(durationSecsRef.current || 180);

      // Calculate progress: start at 5%, reach 90% at the estimated time
      const calculatedProgress = 5 + (85 * (elapsedTime / targetTime));
      const newProgress = Math.min(90, Math.round(calculatedProgress));

      setProgress(newProgress);

      // Also poll for status
      await pollEpisodeStatus();
    };

    // Start polling immediately
    pollEpisodeStatus();

    // Set up intervals
    progressInterval = setInterval(progressIncrement, 5000); // Every 5 seconds

    return () => {
      clearInterval(progressInterval);
    };
  }, [episodeId, apiClient]);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      onDismiss?.();
    }, 300);
  };

  const getStatusMessage = () => {
    if (status === 'completed') {
      return '🎉 Episode ready!';
    } else if (status === 'failed') {
      return "Your story couldn’t be generated!";
    } else {
      return "Your episode is being generated, Let's watch some other stories!";
    }
  };

  const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };
  const barGradient =
    status === 'completed' ? 'linear-gradient(90deg,#10B981,#34D399)'
      : status === 'failed' ? 'linear-gradient(90deg,#EF4444,#F87171)'
        : 'linear-gradient(90deg,#0065F8,#00CAFF)';
  const iconGradient =
    status === 'completed' ? 'linear-gradient(135deg,#10B981,#059669)'
      : status === 'failed' ? 'linear-gradient(135deg,#EF4444,#DC2626)'
        : 'linear-gradient(135deg,#0A7DFF,#0065F8)';
  const iconSvg =
    status === 'completed' ? (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 13l4 4L19 7" />
      </svg>
    ) : status === 'failed' ? (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 18L18 6M6 6l12 12" />
      </svg>
    ) : (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.2 6 3 11l-.9-2.4c-.3-1.1.3-2.2 1.3-2.5l13.5-4c1.1-.3 2.2.3 2.5 1.3Z" />
        <path d="m6.2 5.3 3.1 3.9" />
        <path d="m12.4 3.4 3.1 4" />
        <path d="M3 11h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      </svg>
    );
  const iconShadow =
    status === 'completed' ? '0 6px 16px rgba(16,185,129,0.30)'
      : status === 'failed' ? '0 6px 16px rgba(239,68,68,0.30)'
        : '0 6px 16px rgba(0,101,248,0.28)';

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, x: 100, y: -20 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: 100, y: -20 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed top-4 right-4 z-[100] w-80"
        >
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              ...FREDOKA,
              background: '#FFFFFF',
              border: '1px solid rgba(11,26,51,0.06)',
              boxShadow: '0px 2px 0px rgba(11,26,51,0.05), 0 16px 40px rgba(11,26,51,0.22)',
            }}
          >
            {/* Progress Bar at top */}
            {status !== 'failed' && (
              <div style={{ height: 4, background: 'rgba(0,101,248,0.12)' }}>
                <motion.div
                  style={{ height: '100%', background: barGradient }}
                  initial={{ width: '0%' }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                />
              </div>
            )}

            {/* Content */}
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                {/* Icon and Text */}
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {/* Animated Icon */}
                  <div
                    className="flex-shrink-0 flex items-center justify-center relative"
                    style={{ width: 40, height: 40, borderRadius: '50%', background: iconGradient, boxShadow: iconShadow }}
                  >
                    {status === 'processing' && (
                      <motion.div
                        className="absolute inset-0 rounded-full"
                        style={{ background: 'rgba(255,255,255,0.35)' }}
                        animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0, 0.5] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                    )}
                    <span className="relative z-10" style={{ display: 'flex' }}>{iconSvg}</span>
                  </div>

                  {/* Text Content */}
                  <div className="flex-1 min-w-0">
                    <p style={{ ...FREDOKA, color: '#0B1A33', fontSize: 14, fontWeight: 700, lineHeight: 1.35, margin: 0, wordBreak: 'break-word' }}>
                      {getStatusMessage()}
                    </p>
                    {status === 'processing' && (
                      <p style={{ ...FREDOKA, color: '#5A6B85', fontSize: 12, fontWeight: 600, margin: '4px 0 0' }}>
                        {progress}% complete{durationSecs ? ` · usually ${getStoryGenerationEstimate(durationSecs)}` : ''}
                      </p>
                    )}
                    {status === 'failed' && (
                      <p style={{ ...FREDOKA, color: '#5A6B85', fontSize: 12, margin: '4px 0 0' }}>
                        Please try again or check your library
                      </p>
                    )}
                  </div>
                </div>

                {/* Dismiss Button */}
                <button
                  onClick={handleDismiss}
                  aria-label="Dismiss"
                  className="flex-shrink-0 transition active:scale-90 p-1 rounded-lg"
                  style={{ color: '#8CA0BC' }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.4} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EpisodeProgressNotification;
